import Axios from "axios";
import useAnalytics from "hooks/analytics";
import useAuthUser from "hooks/auth-user";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import Appointment from "models/appointment";
import SuggestedMedicalTest, {
    sortSuggestedMedicalTest,
} from "models/suggested-medical-test";
import React from "react";
import { useAsync } from "react-async-hook";
import { NotificationManager } from "react-notifications";
import { useHistory } from "react-router-dom";
import useLanguage from "../../../../../hooks/language";
import ENDPOINTS from "../../../../../utils/endpoints";
import ROUTES from "../../../../../utils/routes";
import { trackBookingFlowRecommendedTestsSelected } from "../../../../../utils/analytics/booking-flow-tracking";
import EligibleRecommendationLayout, {
    EligibleRecommendationContent,
} from "./eligible-recommendation-layout";
import OutcomeRecommendationLayout, {
    RecommendationContent,
} from "./outcome-recommendation-layout";
import "./recommendations.css";

export type HpvRecommendation =
    | "eligible"
    | "eligible_immunosuppressed"
    | "consult_symptoms"
    | "consult_followup"
    | "consult_clarify"
    | "not_recommended_recent_hpv_test"
    | "not_recommended_recent_pap_test"
    | "not_recommended_no_cervix"
    | "not_recommended_under_25";

interface HpvRecommendationsProps {
    upcomingAppointment?: Appointment;
    hpvRecommendation: HpvRecommendation;
    onSubmit: (tests: SuggestedMedicalTest[]) => void;
}

export default function HpvRecommendations({
    upcomingAppointment,
    hpvRecommendation,
    onSubmit,
}: HpvRecommendationsProps): React.ReactElement {
    const { push } = useHistory();
    const [authUser, setAuthUser] = useAuthUser();
    const [analytics] = useAnalytics();
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const { translations, language } = useLanguage();
    const t = translations.recommendationsSection.hpv;
    const tRecommendations = translations.recommendationsSection;
    const lastUserQuestionnaireId = authUser?.lastUserQuestionnaire?.id;
    const {
        result: suggestedMedicalTests,
        loading: isLoadingMedicalTests,
    } = useAsync(
        async (userQuestionnaireId?: number | null) => {
            if (!userQuestionnaireId) {
                return;
            }

            const { data } = await Axios.get<SuggestedMedicalTest[]>(
                ENDPOINTS.MEDICAL_TESTS_RECOMMENDATIONS(userQuestionnaireId)
            );
            return sortSuggestedMedicalTest(data, language);
        },
        [lastUserQuestionnaireId, language]
    );

    const [isUpdatingAppointment, setIsUpdatingAppointment] = React.useState(
        false
    );

    const onBackToHome = () => {
        push(ROUTES.HOME);
    };

    const updateAppointment = async (
        userQuestionnaireId: number,
        chosenMedicalTests: { medicalTestId: number }[]
    ) => {
        if (!upcomingAppointment) {
            return;
        }

        setIsUpdatingAppointment(true);

        try {
            const body = {
                userQuestionnaireId,
                chosenMedicalTests,
            };

            await Axios.put(
                ENDPOINTS.UPDATE_APPOINTMENT_TESTS(upcomingAppointment.id),
                body
            );
            const { data } = await Axios.get(ENDPOINTS.ME);
            setAuthUser(data);
            NotificationManager.success(tRecommendations.appointmentUpdated);
            push(ROUTES.HOME);
        } catch {
            setIsUpdatingAppointment(false);
        }
    };

    const handleContinue = () => {
        if (upcomingAppointment) {
            if (!authUser?.lastUserQuestionnaire || !suggestedMedicalTests) {
                return;
            }

            const chosenMedicalTests = suggestedMedicalTests.map(
                ({ medicalTest }) => ({
                    medicalTestId: medicalTest.id,
                })
            );

            updateAppointment(
                authUser.lastUserQuestionnaire.id,
                chosenMedicalTests
            );
            return;
        }

        if (suggestedMedicalTests) {
            if (isBookingFlowRedesign) {
                trackBookingFlowRecommendedTestsSelected(analytics);
            }
            onSubmit(suggestedMedicalTests);
        }
    };

    if (
        hpvRecommendation === "eligible" ||
        hpvRecommendation === "eligible_immunosuppressed"
    ) {
        const eligibleContent: EligibleRecommendationContent = {
            ...t.eligible,
            negativeResultText:
                hpvRecommendation === "eligible_immunosuppressed"
                    ? t.eligible.eligible_immunosuppressed.negativeResultText
                    : t.eligible.eligible.negativeResultText,
        };

        return (
            <div className="hpv-recommendations__container">
                <EligibleRecommendationLayout
                    content={eligibleContent}
                    onSubmit={handleContinue}
                    disabled={!suggestedMedicalTests}
                    loading={isLoadingMedicalTests || isUpdatingAppointment}
                />
            </div>
        );
    }

    const content: RecommendationContent = {
        title: t.recommendation,
        whyThisResultTitle: t.outcome.shared.whyThisResultTitle,
        whatToDoNowTitle: t.outcome.shared.whatToDoNowTitle,
        ctaLabel: t.outcome.shared.ctaLabel,
        ...t.outcome[hpvRecommendation],
    };

    return (
        <div className="hpv-recommendations__container">
            <OutcomeRecommendationLayout
                content={content}
                onSubmit={onBackToHome}
            />
        </div>
    );
}
